import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, X, Linkedin, Facebook, Sparkles, Clock, Share2, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { motion } from 'framer-motion';
import BlogImage from '../components/ui/BlogImage';
import { comprehensiveBlogPosts } from '../data/blogContent';

const blogPosts = comprehensiveBlogPosts.length > 0 ? comprehensiveBlogPosts : [
    {
      id: 1,
      title: 'Unlocking the Power of AI for Your Social Media Content',
      excerpt: 'Stop guessing what content will perform well. Learn how Quixocial\'s AI engine can analyze your brand and generate stunning, high-engagement posts, giving you more time to focus on strategy.',
      author: 'The Quixocial Team',
      date: 'September 26, 2025',
      category: 'AI & Technology',
      readTime: '6 min read',
      image: '/images/blog/ai-unlock.svg',
      featured: true,
      content: `
        <p class="text-lg text-gray-700 mb-6">In today\'s fast-paced digital world, staying relevant on social media requires a constant stream of fresh, engaging content. But who has the time to brainstorm, create, and design posts every single day? This is where Artificial Intelligence is changing the game for marketers, agencies, and business owners.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Challenge of Modern Content Creation</h2>
        <p class="text-lg text-gray-700 mb-6">The demand for high-quality content is higher than ever. Your audience expects visually appealing graphics, witty captions, and posts that resonate with them on a personal level. Meeting these expectations consistently across multiple platforms can lead to creative burnout and stretched resources.</p>
        <blockquote class="border-l-4 border-blue-500 pl-4 py-2 my-6">
          <p class="text-xl italic text-gray-800">“The biggest challenge for marketers is not a lack of ideas, but a lack of time to execute them.”</p>
        </blockquote>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">How AI Transforms Your Workflow</h2>
        <p class="text-lg text-gray-700 mb-6">Instead of starting from a blank page, imagine having a creative partner that understands your brand. Quixocial\'s AI engine does just that. By analyzing your brand kit, target audience, and performance goals, our AI can:</p>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li>Generate dozens of unique post ideas in seconds.</li>
          <li>Create stunning visuals and ad creatives tailored to your brand identity.</li>
          <li>Write compelling captions that drive engagement and conversions.</li>
          <li>Suggest the best times to post for maximum reach.</li>
        </ul>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">More Than Just Automation</h2>
        <p class="text-lg text-gray-700 mb-6">This isn\'t about replacing human creativity; it\'s about augmenting it. AI handles the repetitive, time-consuming tasks, freeing you up to focus on what truly matters: building your brand, engaging with your community, and driving business results. It\'s time to stop guessing and start creating with intelligence.</p>
      `
    },
    {
      id: 2,
      title: '5 Time-Saving Tips for Managing Multiple Client Accounts',
      excerpt: 'Juggling multiple social media clients can be chaotic. Discover five practical tips for streamlining your workflow, from centralized content libraries to automated reporting, all within Quixocial.',
      author: 'The Quixocial Team',
      date: 'September 22, 2025',
      category: 'Social Media Tips',
      readTime: '8 min read',
      image: '/images/blog/agency-tips.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">As a social media manager for an agency, your day is a constant balancing act. Switching between different client accounts, brand voices, and content calendars can be overwhelming. But with the right tools and strategies, you can turn chaos into a streamlined, efficient workflow.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Centralize Everything with a Multi-Client Dashboard</h2>
        <p class="text-lg text-gray-700 mb-6">Stop logging in and out of different accounts. A unified dashboard, like the one in Quixocial, allows you to view all your clients in one place. This gives you a high-level overview of all your accounts at a glance.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Use a Shared Media Library</h2>
        <p class="text-lg text-gray-700 mb-6">Don\'t waste time hunting for assets. A centralized media library for each client ensures that all approved logos, images, and videos are in one easy-to-access location. This also helps in maintaining brand consistency.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Master the Art of Bulk Scheduling</h2>
        <p class="text-lg text-gray-700 mb-6">Dedicate specific blocks of time to schedule content for the upcoming week or month. Using a bulk scheduling tool, you can upload and schedule hundreds of posts at once, saving you countless hours.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Create and Reuse Content Templates</h2>
        <p class="text-lg text-gray-700 mb-6">Many social media posts follow a similar format. Create templates for different types of content (e.g., testimonials, promotions, blog post announcements) to speed up the creation process.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Automate Your Reporting</h2>
        <p class="text-lg text-gray-700 mb-6">Manually creating reports for each client is a huge time sink. Use a tool that automatically generates and sends professional, white-labeled reports to your clients, showcasing the amazing results you\'re delivering.</p>
      `
    },
    {
      id: 3,
      title: 'From 2D to 3D: The Future of E-commerce is Here',
      excerpt: 'Product photos are no longer enough. Explore how Quixocial\'s new feature converts your 2D product images into interactive 3D models for AR, and why this is a game-changer for online stores.',
      author: 'The Quixocial Team',
      date: 'September 18, 2025',
      category: 'Product Updates',
      readTime: '5 min read',
      image: '/images/blog/2d-to-3d.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">For years, high-quality product photography has been the cornerstone of e-commerce. But in an increasingly immersive digital world, static images are starting to feel... well, flat. The next frontier in online shopping is Augmented Reality (AR), and it\'s more accessible than you think.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Introducing 2D-to-3D Conversion</h2>
        <p class="text-lg text-gray-700 mb-6">We\'re thrilled to announce a groundbreaking new feature in Quixocial: the ability to convert your existing 2D product photos into fully interactive 3D models. With just a few clicks, you can create an AR experience that allows customers to view your products from any angle, right in their own space.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Why This is a Game-Changer for E-commerce</h2>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li><strong>Increased Conversion Rates:</strong> Studies show that AR experiences can increase conversion rates by up to 94% as customers feel more confident in their purchase.</li>
          <li><strong>Reduced Returns:</strong> By allowing customers to visualize products in their own environment, you reduce the likelihood of returns due to size or style issues.</li>
          <li><strong>Enhanced Engagement:</strong> Interactive 3D content is far more engaging than static images, leading to more shares and a stronger brand connection.</li>
        </ul>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">How It Works</h2>
        <p class="text-lg text-gray-700 mb-6">Our AI-powered tool analyzes your product photos and intelligently constructs a 3D model. You can then share this model as a link on your social media, embed it on your website, or use it to create unique AR ad campaigns. The future of e-commerce is here, and it\'s three-dimensional.</p>
      `
    },
    {
      id: 4,
      title: 'Beyond Likes: Choosing the Right Social Media Analytics to Track',
      excerpt: 'Vanity metrics can be misleading. This guide breaks down the key performance indicators (KPIs) you should be tracking to measure real ROI and prove your social media impact to clients or stakeholders.',
      author: 'The Quixocial Team',
      date: 'September 15, 2025',
      category: 'Marketing Strategy',
      readTime: '7 min read',
      image: '/images/blog/analytics-kpis.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">Likes and follower counts are easy to track, but they don\'t tell the whole story. To truly understand your social media performance and prove its value, you need to look beyond these "vanity metrics" and focus on the key performance indicators (KPIs) that align with your business goals.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Engagement Rate</h2>
        <p class="text-lg text-gray-700 mb-6">This is the percentage of your audience that interacts with your content. It\'s a much better indicator of a healthy, interested audience than just follower count. Track not just likes, but also comments, shares, and saves.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Reach vs. Impressions</h2>
        <p class="text-lg text-gray-700 mb-6"><strong>Impressions</strong> are the total number of times your content was displayed. <strong>Reach</strong> is the number of unique people who saw your content. A high impression count with low reach might indicate your content is being shown to the same people repeatedly.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Click-Through Rate (CTR)</h2>
        <p class="text-lg text-gray-700 mb-6">If your goal is to drive traffic to your website, CTR is crucial. It measures the percentage of people who clicked the link in your post. A low CTR might mean your call-to-action needs to be stronger.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Conversion Rate</h2>
        <p class="text-lg text-gray-700 mb-6">This is the ultimate measure of ROI. It tracks the percentage of users who take a desired action after clicking your link, such as making a purchase, signing up for a newsletter, or filling out a contact form.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Focus on What Matters</h2>
        <p class="text-lg text-gray-700 mb-6">By focusing on these more meaningful metrics, you can make smarter decisions about your content strategy and clearly demonstrate the tangible business impact of your social media efforts. Quixocial\'s analytics dashboard makes it easy to track these KPIs and generate insightful reports.</p>
      `
    },
    {
      id: 5,
      title: 'The Psychology of Social Media Engagement: What Really Drives Clicks',
      excerpt: 'Understanding the psychological triggers behind social media engagement can transform your content strategy. Discover the science-backed principles that make content irresistible to your audience.',
      author: 'Quixocial Team',
      date: 'September 12, 2025',
      category: 'Marketing Strategy',
      readTime: '9 min read',
      image: '/images/blog/psychology-engagement.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">What makes someone stop scrolling and engage with your content? The answer lies in understanding the psychological principles that drive human behavior on social media platforms.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Power of Social Proof</h2>
        <p class="text-lg text-gray-700 mb-6">Humans are inherently social creatures. We look to others for cues about what's valuable, interesting, or worth our attention. This is why engagement metrics like likes, shares, and comments are so powerful.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The Curiosity Gap</h2>
        <p class="text-lg text-gray-700 mb-6">Creating a "curiosity gap" in your content is one of the most effective ways to drive engagement. This involves presenting just enough information to pique interest while leaving something to be discovered.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Emotional Triggers That Work</h2>
        <ul class="list-disc list-inside text-lg text-gray-700 mb-6 space-y-2">
          <li><strong>Surprise:</strong> Unexpected content breaks through the noise of predictable feeds.</li>
          <li><strong>Joy:</strong> Positive emotions are highly shareable and create brand affinity.</li>
          <li><strong>Fear of Missing Out (FOMO):</strong> Limited-time offers and exclusive content drive immediate action.</li>
          <li><strong>Belonging:</strong> Content that makes people feel part of a community or movement.</li>
        </ul>
      `
    },
    {
      id: 6,
      title: 'Building a Content Calendar That Actually Works: A Data-Driven Approach',
      excerpt: 'Stop posting randomly and start planning strategically. Learn how to create a content calendar based on audience behavior data, platform algorithms, and business objectives.',
      author: 'Marcus Rodriguez, Content Strategist',
      date: 'September 8, 2025',
      category: 'Social Media Tips',
      readTime: '11 min read',
      image: '/images/blog/content-calendar.svg',
      featured: false,
      content: `
        <p class="text-lg text-gray-700 mb-6">A well-planned content calendar is the backbone of successful social media marketing. But most businesses approach it wrong, focusing on quantity over quality and ignoring crucial data insights.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Start with Audience Analysis</h2>
        <p class="text-lg text-gray-700 mb-6">Before you plan a single post, you need to understand when your audience is most active, what type of content they engage with, and how they behave across different platforms.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">The 80/20 Content Rule</h2>
        <p class="text-lg text-gray-700 mb-6">Follow the 80/20 rule: 80% of your content should provide value (educate, entertain, inspire), while only 20% should be promotional. This builds trust and keeps your audience engaged.</p>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">Platform-Specific Optimization</h2>
        <p class="text-lg text-gray-700 mb-6">Each platform has its own optimal posting frequency, content format, and engagement patterns. Your calendar should reflect these differences rather than using a one-size-fits-all approach.</p>
      `
    }
  ];

const BlogPostPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const post = blogPosts.find(p => p.id === parseInt(id || ''));
  const relatedPosts = blogPosts.filter(p => p.category === post?.category && p.id !== post?.id).slice(0, 2);

  if (!post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">Post not found</h1>
        <p className="text-lg text-muted-foreground mb-8">Sorry, we couldn\'t find the blog post you\'re looking for.</p>
        <Link to="/blog">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white font-sans">
      {/* Modern Header */}
      <header className="relative bg-black/50 backdrop-blur-xl border-b border-gray-700/50 shadow-2xl sticky top-0 z-50">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-pink-600/10"></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <Link to="/blog">
              <Button variant="ghost" className="flex items-center text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Quixocial
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article>
            {/* Article Header */}
            <motion.header 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-12 text-center"
            >
              <div className="mb-6">
                <Badge variant="secondary" className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
                  <BookOpen className="w-4 h-4 mr-2" />
                  {post.category}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
                {post.title}
              </h1>
              <div className="flex items-center justify-center gap-8 text-gray-300">
                <div className="flex items-center">
                  <User className="w-5 h-5 mr-2 text-purple-400" />
                  {post.author}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-pink-400" />
                  {post.date}
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-400" />
                  {post.readTime}
                </div>
              </div>
            </motion.header>

            {/* Featured Image */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-12 relative overflow-hidden rounded-2xl shadow-2xl border border-gray-700/50"
            >
              <BlogImage 
                src={post.image} 
                alt={post.title}
                title={post.title}
                category={post.category}
                className="w-full h-96"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </motion.div>

            {/* Article Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="bg-gray-800/30 backdrop-blur-xl rounded-2xl p-8 md:p-12 border border-gray-700/50 mb-12"
            >
              <div
                className="prose prose-lg prose-invert max-w-none leading-relaxed blog-content"
                style={{
                  '--tw-prose-body': 'rgb(209 213 219)',
                  '--tw-prose-headings': 'rgb(255 255 255)',
                  '--tw-prose-lead': 'rgb(156 163 175)',
                  '--tw-prose-links': 'rgb(168 85 247)',
                  '--tw-prose-bold': 'rgb(255 255 255)',
                  '--tw-prose-counters': 'rgb(156 163 175)',
                  '--tw-prose-bullets': 'rgb(75 85 99)',
                  '--tw-prose-hr': 'rgb(55 65 81)',
                  '--tw-prose-quotes': 'rgb(209 213 219)',
                  '--tw-prose-quote-borders': 'rgb(168 85 247)',
                  '--tw-prose-captions': 'rgb(156 163 175)',
                  '--tw-prose-code': 'rgb(255 255 255)',
                  '--tw-prose-pre-code': 'rgb(209 213 219)',
                  '--tw-prose-pre-bg': 'rgb(17 24 39)',
                  '--tw-prose-th-borders': 'rgb(55 65 81)',
                  '--tw-prose-td-borders': 'rgb(75 85 99)',
                } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </motion.div>

            {/* Share Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 border border-gray-700/50 mb-12"
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center">
                  <Share2 className="w-6 h-6 mr-3 text-purple-400" />
                  <h3 className="text-xl font-semibold text-white">Share this insight</h3>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" size="icon" className="border-gray-600 text-gray-300 hover:bg-blue-600 hover:border-blue-600 hover:text-white transition-all duration-300">
                    <X className="w-5 h-5" />
                  </Button>
                  <Button variant="outline" size="icon" className="border-gray-600 text-gray-300 hover:bg-blue-700 hover:border-blue-700 hover:text-white transition-all duration-300">
                    <Linkedin className="w-5 h-5" />
                  </Button>
                  <Button variant="outline" size="icon" className="border-gray-600 text-gray-300 hover:bg-blue-800 hover:border-blue-800 hover:text-white transition-all duration-300">
                    <Facebook className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </article>

          {relatedPosts.length > 0 && (
            <motion.aside 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-16"
            >
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center">
                  <BookOpen className="w-8 h-8 mr-3 text-purple-400" />
                  Continue Reading
                </h2>
                <p className="text-gray-400 text-lg">Explore more insights and strategies</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost, index) => (
                  <motion.div
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 + (index * 0.1) }}
                  >
                    <Link to={`/blog/${relatedPost.id}`} className="group block">
                      <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] hover:bg-gray-800/70 overflow-hidden h-full">
                        <div className="relative overflow-hidden">
                          <BlogImage 
                            src={relatedPost.image} 
                            alt={relatedPost.title}
                            title={relatedPost.title}
                            category={relatedPost.category}
                            className="w-full h-48 transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        <CardHeader className="pb-2">
                          <Badge variant="outline" className="mb-2 w-fit border-purple-500/30 text-purple-300 bg-purple-500/10">
                            {relatedPost.category}
                          </Badge>
                          <h3 className="text-xl font-bold text-white line-clamp-2 group-hover:text-purple-300 transition-colors duration-300 leading-tight">
                            {relatedPost.title}
                          </h3>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-300 text-sm mb-4 line-clamp-2 leading-relaxed">
                            {relatedPost.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-gray-400">
                            <div className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {relatedPost.author}
                            </div>
                            <div className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {relatedPost.date}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.aside>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlogPostPage;